import { isFrameMessageFrom } from '../../lib/untrusted-frame.js';

/**
 * PDF export for the embedded dashboard viewer.
 *
 * The dashboard renders in a cross-origin kiosk iframe whose URL query holds
 * the viewer's live filter selections (`f_*` params; every Apply is a fresh
 * GET). In viewer mode that page posts those params to us — on load, and on
 * request (`VIEWER_FILTER_BRIDGE_JS` in the API's kiosk.controller.ts) — so
 * the header PDF button can export exactly what is on screen.
 *
 * Trust: a message counts only when it comes from OUR iframe's window and
 * from the kiosk page's origin, and only `f_*` keys survive. The export URL
 * is built here from the viewer's own grant; nothing in the message can
 * change where it points or which credential it carries.
 */

const FILTER_KEY = /^f_[A-Za-z0-9_]+$/u;
/** A tab group's active tab, so the PDF shows the tab on screen. */
const TAB_KEY = 'wdb_tab';
const MAX_QUERY_LENGTH = 4000;

/** Keep only filter params (`f_*`) and the tab-group tab (`wdb_tab`), in
 *  order; drop everything else. */
export function sanitizeFilterQuery(query: string): string {
  const out = new URLSearchParams();
  for (const [k, v] of new URLSearchParams(query)) {
    if (FILTER_KEY.test(k) || k === TAB_KEY) out.append(k, v);
  }
  const s = out.toString();
  return s.length > MAX_QUERY_LENGTH ? '' : s;
}

/** The filter query from a kiosk frame's `sage.kiosk.filters` message, or null. */
export function parseKioskFiltersMessage(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as { type?: unknown; query?: unknown };
  if (d.type !== 'sage.kiosk.filters' || typeof d.query !== 'string') return null;
  return sanitizeFilterQuery(d.query);
}

/** True when `event` is a filters message from this viewer's own kiosk frame. */
export function isKioskFrameMessage(
  event: Pick<MessageEvent, 'source' | 'origin'>,
  frame: HTMLIFrameElement | null,
  frameOrigin: string,
): boolean {
  return isFrameMessageFrom(event, frame) && event.origin === frameOrigin;
}

/** The export URL for this dashboard, with the viewer's grant and filters. */
export function dashboardPdfUrl(
  apiOrigin: string,
  dashboardId: string,
  token: string,
  filterQuery: string,
): string {
  const base = `${apiOrigin}/reporting/api/kiosk/dashboards/${encodeURIComponent(dashboardId)}/export.pdf?token=${encodeURIComponent(token)}`;
  const filters = sanitizeFilterQuery(filterQuery);
  return filters ? `${base}&${filters}` : base;
}

/**
 * Ask the kiosk frame for its current filters. Resolves with the query, or
 * null when it doesn't answer within `timeoutMs` (the caller then exports
 * without filters rather than doing nothing).
 */
export function requestFrameFilters(
  frame: HTMLIFrameElement | null,
  frameOrigin: string,
  timeoutMs = 1000,
): Promise<string | null> {
  return new Promise((resolve) => {
    const win = frame?.contentWindow;
    if (!win) {
      resolve(null);
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onMessage = (event: MessageEvent): void => {
      if (!isKioskFrameMessage(event, frame, frameOrigin)) return;
      const query = parseKioskFiltersMessage(event.data);
      if (query === null) return;
      window.removeEventListener('message', onMessage);
      if (timer !== undefined) clearTimeout(timer);
      resolve(query);
    };
    window.addEventListener('message', onMessage);
    timer = setTimeout(() => {
      window.removeEventListener('message', onMessage);
      resolve(null);
    }, timeoutMs);
    try {
      win.postMessage({ type: 'sage.kiosk.filters.request' }, frameOrigin);
    } catch {
      /* frame gone or origin mismatch — the timeout answers */
    }
  });
}

/** A download filename for a dashboard, as export.pdf's own
 *  Content-Disposition names it. */
export function pdfFilename(dashboardName: string): string {
  return `${dashboardName.replace(/[^\w.-]+/gu, '_') || 'dashboard'}.pdf`;
}

/**
 * Fetch the PDF, then save it. Following a plain link to export.pdf only
 * stays put while the answer is an attachment: an error (expired grant, render
 * failure, unpublished board) is JSON, and the link would replace the whole
 * Sage frame with it. Fetching first means a failure rejects here and the
 * viewer can say so, and only a real PDF is ever saved. The API's CORS policy
 * already admits the SPA origin; the grant rides in the URL, no cookies.
 */
export async function downloadPdf(
  url: string,
  filename: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const res = await fetchImpl(url);
  const type = res.headers.get('content-type') ?? '';
  if (!res.ok || !type.includes('application/pdf')) {
    throw new Error(`PDF export failed (${res.status})`);
  }
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    a.remove();
    // Give the browser time to start the save before freeing the blob.
    setTimeout(() => URL.revokeObjectURL(href), 60_000);
  }
}
